import { default as React } from 'react';
import { useState, useEffect } from 'react';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import InputAdornment from '@mui/material/InputAdornment';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import { makeStyles } from '@mui/styles';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';

const useStyles = makeStyles(theme => ({
  available: {
    color: theme.palette.success.main,
  },
  unavailable: {
    color: theme.palette.error.main,
  },
  checking: {
    color: theme.palette.grey[500],
  },
}));

export const AppUrlField = ({
  onChange,
  rawErrors,
  required,
  formData,
  uiSchema,
  idSchema,
  schema,
}: FieldExtensionComponentProps<string>) => {
  const classes = useStyles();
  const catalogApi = useApi(catalogApiRef);
  const [checking, setChecking] = useState(false);
  const [isAvailable, setIsAvailable] = useState<boolean | null>(null);
  const [errorMessage, setErrorMessage] = useState<string>('');

  // Function to normalize URL for comparison (remove trailing slashes, convert to lowercase)
  const normalizeUrl = (url: string): string => {
    try {
      const urlObj = new URL(url);
      // Remove trailing slash and convert to lowercase for consistent comparison
      return urlObj.href.replace(/\/$/, '').toLowerCase();
    } catch {
      return url.trim().replace(/\/$/, '').toLowerCase();
    }
  };

  // Function to validate URL format
  const isValidUrl = (url: string): boolean => {
    const urlPattern = /^https?:\/\/.+/;
    return urlPattern.test(url);
  };

  useEffect(() => {
    const checkAvailability = async () => {
      if (!formData || formData.trim() === '') {
        setIsAvailable(null);
        setErrorMessage('');
        return;
      }

      // Validate URL format first
      if (!isValidUrl(formData)) {
        setIsAvailable(false);
        setErrorMessage('Please enter a valid HTTP or HTTPS URL');
        return;
      }

      setChecking(true);
      setErrorMessage('');

      try {
        const normalizedUrl = normalizeUrl(formData);

        // Query catalog for all components with view-url annotation
        const { items } = await catalogApi.getEntities({
          filter: {
            kind: 'Component',
          },
        });

        // Check if any entity has this URL
        const existingApp = items.find(item => {
          const viewUrl = item.metadata?.annotations?.['backstage.io/view-url'];
          if (viewUrl) {
            const existingNormalizedUrl = normalizeUrl(viewUrl);
            return existingNormalizedUrl === normalizedUrl;
          }
          return false;
        });

        if (existingApp) {
          // URL is already registered
          setIsAvailable(false);
          const appName =
            existingApp.metadata?.title || existingApp.metadata?.name;
          setErrorMessage(
            `This URL is already registered for app: "${appName}"`,
          );
        } else {
          // URL is available
          setIsAvailable(true);
          setErrorMessage('');
        }
      } catch (error) {
        // Error querying catalog - allow user to continue
        setIsAvailable(null);
        setErrorMessage(
          'Unable to verify availability. You can continue, and it will be validated on submission.',
        );
      } finally {
        setChecking(false);
      }
    };

    // Debounce the check
    const timeoutId = setTimeout(() => {
      checkAvailability();
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [formData, catalogApi]);

  const getEndAdornment = () => {
    if (checking) {
      return (
        <InputAdornment position="end">
          <CircularProgress size={20} className={classes.checking} />
        </InputAdornment>
      );
    }
    if (isAvailable === true) {
      return (
        <InputAdornment position="end">
          <CheckCircleIcon className={classes.available} />
        </InputAdornment>
      );
    }
    if (isAvailable === false) {
      return (
        <InputAdornment position="end">
          <ErrorIcon className={classes.unavailable} />
        </InputAdornment>
      );
    }
    return null;
  };

  const getHelperText = () => {
    if (checking) {
      return <span className={classes.checking}>Checking availability...</span>;
    }
    if (isAvailable === true) {
      return (
        <span className={classes.available}>
          ✓ This URL is available for registration
        </span>
      );
    }
    if (errorMessage) {
      return <span className={classes.unavailable}>{errorMessage}</span>;
    }
    if (rawErrors && rawErrors.length > 0) {
      return (
        <span className={classes.unavailable}>{rawErrors.join(', ')}</span>
      );
    }
    return (
      schema.description ||
      uiSchema?.['ui:description'] ||
      'Enter the full URL where your application is accessible'
    );
  };

  return (
    <FormControl
      margin="normal"
      required={required}
      error={rawErrors?.length > 0 || isAvailable === false}
      fullWidth
    >
      <TextField
        id={idSchema?.$id}
        label={schema.title || 'Application URL'}
        value={formData || ''}
        onChange={e => onChange(e.target?.value)}
        required={required}
        error={rawErrors?.length > 0 || isAvailable === false}
        InputProps={{
          endAdornment: getEndAdornment(),
        }}
        placeholder={
          uiSchema?.['ui:placeholder'] || 'https://your-app.example.com'
        }
      />
      <FormHelperText>{getHelperText()}</FormHelperText>
    </FormControl>
  );
};
